/* sugar:assets/custom-table.js@2.1.3 */
/**
 * Custom Table — builds the grid from authored column/row/cell blocks.
 *
 * One copy of this file serves every custom table on the page. The engine
 * reparents the server-rendered blocks into the grid, places spans, backfills
 * blank cells, sizes the column tracks, and wires the sideways-scroll extras
 * (pinned columns, scroll indicators). Re-running it on a root is
 * safe: cells are homed back to their source rows first, so the theme editor
 * can re-render.
 */

(function () {
  'use strict';

  if (window.SugarCC) return;

  // Setup is two halves, and the order between them is the point. layout()
  // only writes: it moves cells, sets attributes and custom properties and
  // builds the indicator without asking the browser for a single size.
  // measure() only then reads. Any read that follows a write makes the
  // browser lay the whole page out again, so mixing the two, per table, cost
  // a page of five tables dozens of layouts during load. The sweep goes one
  // step further and runs layout() for every table before measure() for any,
  // so one layout serves them all. measure() itself has two beats, because
  // the bleed it applies in the first moves the edges the second reads.

  var SKIP = { STYLE: 1, SCRIPT: 1, TEMPLATE: 1, LINK: 1 };

  // Columns and rows can be shown on one screen size only (data-cc-show). The
  // table is built for the screen it is on, the theme's 750px breakpoint, and
  // rebuilt when a window crosses it, so stripes, first and last rows, spans and
  // column widths always describe what is actually showing.
  var PHONE = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  function shownHere(el, phone) {
    var s = el.getAttribute('data-cc-show');
    if (!s) return true;
    return s === 'mobile' ? phone : !phone;
  }

  function num(el, name) {
    return parseInt(el.getAttribute(name) || '0', 10) || 0;
  }

  function markEdges(box) {
    var kids = Array.prototype.slice.call(box.children).filter(function (k) { return !SKIP[k.tagName]; });
    Array.prototype.forEach.call(box.querySelectorAll(':scope > [data-cc-first], :scope > [data-cc-last]'), function (k) {
      k.removeAttribute('data-cc-first');
      k.removeAttribute('data-cc-last');
    });
    if (kids.length) {
      kids[0].setAttribute('data-cc-first', '');
      kids[kids.length - 1].setAttribute('data-cc-last', '');
    }
  }

  function blankCell() {
    var ph = document.createElement('div');
    ph.className = 'sugar-custom-table__cell';
    ph.setAttribute('data-cc-cell-ph', '');
    ph.setAttribute('aria-hidden', 'true');
    return ph;
  }

  // Only touches the attribute when the answer changed, so a measurement that
  // lands where it was leaves the page clean and the next read costs nothing.
  function toggleAttr(el, name, on) {
    if (on) { if (!el.hasAttribute(name)) el.setAttribute(name, ''); }
    else if (el.hasAttribute(name)) el.removeAttribute(name);
  }

  /* ---- Write phase ------------------------------------------------------ */

  // Builds the table and returns a record of what measure() needs, or nothing
  // when the root has no table to build.
  function layout(root) {
    var source = root.querySelector(':scope > .sugar-custom-table__source');
    var grid = root.querySelector('[data-cc-grid]');
    if (!source || !grid) return null;
    var phone = !!(PHONE && PHONE.matches);
    var srcRows = Array.prototype.slice.call(source.querySelectorAll('[data-cc-row]'));
    // Every cell is stamped with its row and its place in that row the first time
    // through, while all of them still sit where the server put them. A rebuild
    // homes every cell back in that order before placing anything, wherever the
    // last build left it: in the grid, in a column now hidden, or in its row.
    srcRows.forEach(function (row, ri) {
      var own = row.querySelectorAll(':scope > [data-cc-cell]');
      for (var q = 0; q < own.length; q++) {
        if (!own[q].hasAttribute('data-cc-home')) {
          own[q].setAttribute('data-cc-home', ri);
          own[q].setAttribute('data-cc-idx', q);
        }
      }
    });
    if (root.hasAttribute('data-cc-ready')) {
      root.querySelectorAll('[data-cc-cell-ph]').forEach(function (p) { p.remove(); });
      var homing = Array.prototype.slice.call(root.querySelectorAll('[data-cc-cell][data-cc-home]'));
      homing.sort(function (a, b) {
        return (num(a, 'data-cc-home') - num(b, 'data-cc-home')) || (num(a, 'data-cc-idx') - num(b, 'data-cc-idx'));
      });
      homing.forEach(function (cell) {
        var home = srcRows[num(cell, 'data-cc-home')];
        if (home) home.appendChild(cell);
      });
    }
    // Columns keep their authored order through rebuilds, even one that left a
    // hidden column behind in the source.
    var cols = Array.prototype.slice.call(root.querySelectorAll('[data-cc-col]'));
    cols.forEach(function (col, i) { if (!col.hasAttribute('data-cc-ci')) col.setAttribute('data-cc-ci', i); });
    cols.sort(function (a, b) { return num(a, 'data-cc-ci') - num(b, 'data-cc-ci'); });
    if (!cols.length) return null;
    var vis = cols.filter(function (c) { return shownHere(c, phone); });
    var rows = srcRows.filter(function (r) { return shownHere(r, phone); });
    var responsive = cols.concat(srcRows).some(function (el) { return el.hasAttribute('data-cc-show'); });
    toggleAttr(root, 'data-cc-responsive', responsive);
    var R = rows.length;
    cols.forEach(function (col) {
      if (vis.indexOf(col) !== -1) return;
      if (col.parentNode !== source) source.appendChild(col);
      col.removeAttribute('data-col-first');
      col.removeAttribute('data-col-last');
    });
    vis.forEach(function (col, i) {
      col.style.gridColumn = (i + 1) + '';
      col.style.gridRow = '1 / span ' + (R + 1);
      if (i === 0) col.setAttribute('data-col-first', ''); else col.removeAttribute('data-col-first');
      if (i === vis.length - 1) col.setAttribute('data-col-last', ''); else col.removeAttribute('data-col-last');
      grid.appendChild(col);
      var header = col.querySelector(':scope > [data-cc-header]');
      if (header) markEdges(header);
      col.removeAttribute('data-flat-left');
      col.removeAttribute('data-flat-right');
    });
    // No column has header content: flag the root so the stylesheet collapses the header
    // row instead of leaving its padding as an empty band above the first row.
    var anyHead = vis.some(function (c) {
      var h = c.querySelector(':scope > [data-cc-header]');
      return !!h && Array.prototype.some.call(h.children, function (k) { return !SKIP[k.tagName]; });
    });
    if (anyHead) root.removeAttribute('data-cc-no-head'); else root.setAttribute('data-cc-no-head', '');
    vis.forEach(function (col, i) {
      if (col.getAttribute('data-cc-bg') !== 'rows') return;
      if (i > 0 && vis[i - 1].hasAttribute('data-cc-bg')) col.setAttribute('data-flat-left', '');
      if (i < vis.length - 1 && vis[i + 1].hasAttribute('data-cc-bg')) col.setAttribute('data-flat-right', '');
    });
    // Stripes count the rows showing. A Mobile only row keeps the stripe of the row
    // above it: it is that row's extra line, moved under it on a phone.
    var stripe = -1;
    rows.forEach(function (row, ri) {
      if (!(row.getAttribute('data-cc-show') === 'mobile' && stripe >= 0)) stripe++;
      var odd = stripe % 2 === 1;
      // Cells keep the columns they were authored for on every screen: a cell in
      // a column hidden here hides with it, and a span counts hidden columns too,
      // so a duplicated row keeps its cells lined up whatever it is shown on.
      var cells = Array.prototype.slice.call(row.querySelectorAll(':scope > [data-cc-cell]'));
      var ci = 0;
      var qi = 0;
      while (ci < cols.length) {
        var cell = cells[qi];
        var span = 1;
        if (cell) {
          span = parseInt(cell.getAttribute('data-cc-span') || '1', 10) || 1;
          if (span < 1) span = 1;
          if (span > cols.length - ci) span = cols.length - ci;
        }
        var cover = [];
        for (var k = ci; k < ci + span; k++) {
          if (vis.indexOf(cols[k]) !== -1) cover.push(cols[k]);
        }
        ci += span;
        qi++;
        if (!cover.length) continue;
        if (cell) markEdges(cell);
        else cell = blankCell();
        var col = cover[0];
        var rowBg = row.getAttribute('data-row-bg');
        if (!cell.hasAttribute('data-own-bg')) {
          if (rowBg) {
            cell.style.backgroundColor = rowBg;
            cell.setAttribute('data-row-painted', '');
          } else if (cell.hasAttribute('data-row-painted')) {
            cell.style.backgroundColor = '';
            cell.removeAttribute('data-row-painted');
          }
        }
        var rowSep = row.getAttribute('data-row-sep');
        if (rowSep) cell.setAttribute('data-row-sep', rowSep);
        else cell.removeAttribute('data-row-sep');
        var rowSepColor = row.getAttribute('data-row-sep-color');
        if (rowSepColor) cell.style.setProperty('--cc-row-sep-color', rowSepColor);
        else cell.style.removeProperty('--cc-row-sep-color');
        if (ri === 0) cell.setAttribute('data-row-first', ''); else cell.removeAttribute('data-row-first');
        if (ri === R - 1) cell.setAttribute('data-row-last', ''); else cell.removeAttribute('data-row-last');
        if (odd) cell.setAttribute('data-row-odd', ''); else cell.removeAttribute('data-row-odd');
        if (cover.length > 1) {
          var vi = vis.indexOf(col);
          cell.style.gridColumn = (vi + 1) + ' / span ' + cover.length;
          cell.style.gridRow = (ri + 2) + '';
          /* A cell across several columns is a banner: its content takes the
             full width and keeps its own alignment, not the first column's. */
          cell.style.alignItems = 'stretch';
          cell.style.textAlign = '';
          if (vi === 0) cell.setAttribute('data-span-start', ''); else cell.removeAttribute('data-span-start');
          if (vi + cover.length === vis.length) cell.setAttribute('data-span-end', ''); else cell.removeAttribute('data-span-end');
          grid.appendChild(cell);
          for (var sp = 0; sp < cover.length; sp++) {
            var under = blankCell();
            under.setAttribute('data-span-under', '');
            cover[sp].appendChild(under);
          }
        } else {
          cell.style.gridColumn = '';
          cell.style.gridRow = '';
          cell.style.alignItems = '';
          cell.style.textAlign = '';
          cell.removeAttribute('data-span-start');
          cell.removeAttribute('data-span-end');
          if (!cell.getAttribute('style')) cell.removeAttribute('style');
          col.appendChild(cell);
        }
      }
    });
    grid.style.gridTemplateRows = 'repeat(' + (R + 1) + ', auto)';
    var scrollMode = root.getAttribute('data-cc-layout') === 'scroll';
    var globalTrack = root.getAttribute('data-col-sizing') === 'fit' ? 'max-content' : '1fr';
    var deskTracks = vis.map(function (c) { return c.getAttribute('data-width') || globalTrack; });
    // Scroll mode keeps the authored track and only adds a floor under it, so a
    // table that still fits looks exactly as it does in Shrink to fit. The floor
    // reads from --cc-min, which the stylesheet swaps per breakpoint — one track
    // list serves every width. Every track carries it, so the grid's own minimum
    // width below is exact and columns can never spill past it.
    function scrollTrack(t) {
      return 'minmax(var(--cc-min, 150px), ' + t + ')';
    }
    var mobTracks = vis.map(function (c, i) { return c.getAttribute('data-width-mobile') || deskTracks[i]; });
    var scrollTracks = deskTracks.map(scrollTrack);
    // A column's own phone floor (Minimum width (mobile)) replaces the table's for that column
    // only; the grid's width then adds up the floors one by one instead of columns × minimum.
    var mobFloors = vis.map(function (c) { var m = parseInt(c.getAttribute('data-min-mobile') || '0', 10); return m > 0 ? m + 'px' : ''; });
    var scrollTracksMob = mobTracks.map(function (t, i) { return mobFloors[i] ? 'minmax(' + mobFloors[i] + ', ' + t + ')' : scrollTrack(t); });
    if (mobFloors.some(Boolean)) {
      root.style.setProperty('--cc-floors-m', mobFloors.map(function (f) { return f || 'var(--cc-min, 150px)'; }).join(' + '));
    } else {
      root.style.removeProperty('--cc-floors-m');
    }
    root.style.setProperty('--cc-tpl', deskTracks.join(' '));
    root.style.setProperty('--cc-tpl-m', mobTracks.join(' '));
    root.style.setProperty('--cc-tpl-s', scrollTracks.join(' '));
    root.style.setProperty('--cc-tpl-sm', scrollTracksMob.join(' '));
    var extTop = 0, extBottom = 0;
    vis.forEach(function (c) {
      extTop = Math.max(extTop, parseInt(c.getAttribute('data-ext-top') || '0', 10) || 0);
      extBottom = Math.max(extBottom, parseInt(c.getAttribute('data-ext-bottom') || '0', 10) || 0);
      var pokes = c.querySelectorAll('[data-cc-header] [data-img-poke]');
      for (var pk = 0; pk < pokes.length; pk++) {
        extTop = Math.max(extTop, parseInt(pokes[pk].getAttribute('data-img-poke'), 10) || 0);
      }
    });
    root.style.setProperty('--cc-ext-pad-top', extTop + 'px');
    root.style.setProperty('--cc-ext-pad-bottom', extBottom + 'px');
    var scroller = root.querySelector('[data-cc-scroll]') || grid;
    var pins = vis.filter(function (c) { return c.hasAttribute('data-pin'); });
    root.style.setProperty('--cc-cols', String(vis.length));
    if (pins.length) root.setAttribute('data-cc-pinned', '');
    else root.removeAttribute('data-cc-pinned');
    root.setAttribute('data-cc-ready', '');
    if (!scrollMode) {
      root.removeAttribute('data-cc-of');
      scroller.style.scrollPaddingLeft = '';
    }

    var t = {
      root: root,
      grid: grid,
      scroller: scroller,
      // Edge to edge measures against the screen, so it is only safe when the table
      // already spans the page. Measured on the host, which the table's own negative
      // margins can't move, so the answer stays stable once the bleed is applied.
      host: root.parentElement,
      cols: vis,
      pins: pins,
      scrollMode: scrollMode,
      headEl: grid.querySelector('.sugar-custom-table__col > [data-cc-header]'),
      paint: null
    };

    var oldInd = root.querySelector(':scope > .sugar-custom-table__indicator');
    if (oldInd) oldInd.remove();
    var indType = root.getAttribute('data-scroll-indicator');
    // Swipe text is server-rendered above the table (or moved below it by the stylesheet) (it must not push the table down
    // once the script runs); the stylesheet shows it from data-cc-of. The indicator
    // is built here, blank, and first painted by measure() with the rest of the
    // scroll numbers; on its own scroll events it reads and paints itself.
    if (indType && indType !== 'none' && indType !== 'text' && indType !== 'text_below' && scrollMode) {
      var ind = document.createElement('div');
      ind.className = 'sugar-custom-table__indicator sugar-custom-table__indicator--' + indType;
      if (indType === 'bar') {
        var thumb = document.createElement('span');
        thumb.className = 'sugar-custom-table__indicator-thumb';
        ind.appendChild(thumb);
        t.paint = function (m) {
          var frac = m.sw > 0 ? m.cw / m.sw : 1;
          var max = m.sw - m.cw;
          var pos = max > 0 ? m.sl / max : 0;
          thumb.style.width = (frac * 100) + '%';
          thumb.style.marginLeft = (pos * (1 - frac) * 100) + '%';
        };
      } else {
        var free = vis.filter(function (c) { return !c.hasAttribute('data-pin'); });
        if (free.length > 1) {
          for (var d = 0; d < free.length; d++) {
            var dot = document.createElement('span');
            dot.className = 'sugar-custom-table__indicator-dot';
            ind.appendChild(dot);
          }
          var dots = ind.children;
          t.paint = function (m) {
            var max = m.sw - m.cw;
            var active = max > 0 ? Math.round(m.sl / max * (dots.length - 1)) : 0;
            for (var k = 0; k < dots.length; k++) {
              if (k === active) dots[k].setAttribute('data-active', '');
              else dots[k].removeAttribute('data-active');
            }
          };
        }
      }
      root.appendChild(ind);
    }
    // One scroll listener per table, swapped on a rebuild so an earlier build's
    // indicator stops painting.
    if (root.__ccScrollFn) scroller.removeEventListener('scroll', root.__ccScrollFn);
    root.__ccScrollFn = null;
    if (t.paint) {
      root.__ccScrollFn = function () { t.paint(readBox(scroller)); };
      scroller.addEventListener('scroll', root.__ccScrollFn, { passive: true });
    }

    wire(t);
    return t;
  }

  /* ---- Read phase ------------------------------------------------------- */

  // Beat one: the header band and whether the host spans the page. Neither
  // depends on anything measure() writes.
  function readBand(t) {
    return t.headEl ? Math.ceil(t.headEl.getBoundingClientRect().height) : -1;
  }
  function writeBand(t, h) {
    if (h >= 0) t.root.style.setProperty('--cc-head-h', h + 'px');
  }
  function readWide(t) {
    // Layout width, not a rect: a host that is transformed, off-canvas or still
    // sliding in (the cart drawer) must not read as narrow.
    return !!t.host && t.host.offsetWidth >= document.documentElement.clientWidth * 0.66;
  }
  function writeWide(t, wide) {
    toggleAttr(t.root, 'data-cc-bleed', wide);
  }

  // Beat two: widths. The bleed from beat one moves the table's edges, so
  // these are only right once it is applied. Pin offsets accumulate from the
  // column widths, so every width is read into the record before any pin is
  // written.
  function readBox(scroller) {
    return { sw: scroller.scrollWidth, cw: scroller.clientWidth, sl: scroller.scrollLeft };
  }
  function readScroll(t) {
    var m = readBox(t.scroller);
    var gs = getComputedStyle(t.grid);
    m.gap = parseFloat(gs.columnGap) || 0;
    m.pad = (parseFloat(getComputedStyle(t.scroller).paddingLeft) || 0) + (parseFloat(gs.paddingLeft) || 0);
    m.widths = t.pins.map(function (c) { return c.getBoundingClientRect().width; });
    return m;
  }
  function writePins(t, m) {
    var left = 0;
    var snap = m.pad;
    t.pins.forEach(function (c, i) {
      c.style.setProperty('--cc-pin-left', left + 'px');
      var w = m.widths[i] + m.gap;
      left += w;
      snap += w;
    });
    t.scroller.style.scrollPaddingLeft = snap ? snap + 'px' : '';
  }
  // Marks the table as actually overflowing, so the scroll indicator only shows
  // when there is something off screen to indicate.
  function writeScroll(t, m) {
    writePins(t, m);
    toggleAttr(t.root, 'data-cc-of', m.sw - m.cw > 1);
    if (t.paint) t.paint(m);
  }

  // Measures a batch of built tables: all of beat one's reads, then its
  // writes, then all of beat two's reads, then its writes. Two layouts for the
  // batch, however many tables it holds.
  function measure(tables) {
    // A table inside something closed (display: none, a shut popup) measures as
    // zero, so it is left alone here; its observers run these same reads the
    // moment it opens.
    var live = tables.filter(function (t) { return t.root.offsetParent !== null; });
    var bands = live.map(readBand);
    var wides = live.map(readWide);
    live.forEach(function (t, i) { writeBand(t, bands[i]); writeWide(t, wides[i]); });
    var scrolls = live.map(function (t) { return t.scrollMode ? readScroll(t) : null; });
    live.forEach(function (t, i) { if (scrolls[i]) writeScroll(t, scrolls[i]); });
  }

  /* ---- Observers -------------------------------------------------------- */

  // Each callback keeps the same order as measure(): read, write, and for the
  // full remeasure, read again only after the bleed has settled. The hooks
  // live on the root so a resize listener bound by an earlier run reaches the
  // current table, and the observers are torn down before new ones are made.
  function wire(t) {
    var root = t.root;
    root.__ccBand = function () { writeBand(t, readBand(t)); };
    root.__ccSetPins = function () { writePins(t, readScroll(t)); };
    root.__ccMeasure = function () {
      writeWide(t, readWide(t));
      writeScroll(t, readScroll(t));
    };
    if (root.__ccHeadRO) root.__ccHeadRO.disconnect();
    if (window.ResizeObserver && t.headEl) {
      root.__ccHeadRO = new ResizeObserver(function () { root.__ccBand(); });
      root.__ccHeadRO.observe(t.headEl);
    } else if (!root.__ccHeadBound) {
      root.__ccHeadBound = true;
      window.addEventListener('resize', function () { root.__ccBand(); });
    }
    if (root.__ccBoxRO) root.__ccBoxRO.disconnect();
    if (t.scrollMode) {
      if (window.ResizeObserver) {
        root.__ccBoxRO = new ResizeObserver(function () { root.__ccMeasure(); });
        root.__ccBoxRO.observe(t.scroller);
      }
    } else if (window.ResizeObserver && t.host) {
      root.__ccBoxRO = new ResizeObserver(function () { writeWide(t, readWide(t)); });
      root.__ccBoxRO.observe(t.host);
    }
    if (!root.__ccPinBound) {
      root.__ccPinBound = true;
      window.addEventListener('resize', function () { root.__ccMeasure(); });
    }
  }

  /* ---- Entry points ----------------------------------------------------- */

  window.SugarCC = function (root) {
    var t = layout(root);
    if (t) measure([t]);
  };

  // Every table on the page is built before any is measured.
  var sweep = function () {
    var tables = [];
    document.querySelectorAll('.sugar-custom-table:not([data-cc-ready])').forEach(function (r) {
      var t = layout(r);
      if (t) tables.push(t);
    });
    if (tables.length) measure(tables);
  };
  document.addEventListener('shopify:section:load', sweep);

  // Crossing the breakpoint rebuilds only the tables that show a column or row
  // on one screen size; every other table is left exactly as it was built.
  var rebuild = function () {
    var tables = [];
    document.querySelectorAll('.sugar-custom-table[data-cc-ready][data-cc-responsive]').forEach(function (r) {
      var t = layout(r);
      if (t) tables.push(t);
    });
    if (tables.length) measure(tables);
  };
  if (PHONE) {
    if (PHONE.addEventListener) PHONE.addEventListener('change', rebuild);
    else if (PHONE.addListener) PHONE.addListener(rebuild);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  else sweep();
})();
